import { CommonModule } from "@angular/common";
import { Component, ElementRef, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatInputModule } from "@angular/material/input";
import { Router, RouterModule } from "@angular/router";
import { differenceInMinutes, format, isAfter, isBefore } from "date-fns";
import { formatInTimeZone, getTimezoneOffset } from "date-fns-tz";
import {
  BehaviorSubject,
  Observable,
  Subject,
  firstValueFrom,
  map,
} from "rxjs";
import {
  ButtonComponent,
  ButtonVariant,
} from "src/app/common/components/core/button/button.component";
import { DropdownComponent } from "src/app/common/components/core/dropdown/dropdown.component";
import { InputComponent } from "src/app/common/components/core/input/input.component";
import { ConfirmationModalComponent } from "src/app/common/components/core/modal/confirmation-modal/confirmation-modal.component";
import { ClockInOutAfterCurrentTimeError, ClockOutBeforeClockInError, DateFormat, NotClockedOutPreviousDayError } from "src/app/common/constants/core-constants";
import { IntegrationNames } from "src/app/common/enums/integration-names.enum";
import { DropdownItem } from "src/app/common/interfaces/dropdown-item.interface";
import { FacilityIntegrationListItem } from "src/app/common/interfaces/integration.interface";
import { TimeClock } from "src/app/common/interfaces/timeclock.interface";
import { ClockInService } from "src/app/common/services/clock-in.service";
import { FacilityRelationsClient } from "src/app/common/services/facility-relations.client";
import { StorageService } from "src/app/common/services/storage.service";
import { TimeClockClient } from "src/app/common/services/time-clock.client";
import { TimeDetailClient } from "src/app/common/services/time-detail.client";
import { UserClient } from "src/app/common/services/user.client";

@Component({
  selector: "app-clock-in",
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    RouterModule,
    DropdownComponent,
    InputComponent,
    ReactiveFormsModule,
    MatInputModule,
  ],
  templateUrl: "./clock-in.component.html",
  styleUrls: ["./clock-in.component.scss"],
})
export class ClockInComponent implements OnInit {
  @Input() facilities!: Observable<DropdownItem[]>;
  form: FormGroup = new FormGroup({});
  reloadAmPm$ = new Subject<void>();
  buttonVariant = ButtonVariant;
  minuteMin = 0;
  hourMin = 1;
  minuteMax = 59;
  hourMax = 12;
  elapsedHours: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  elapsedMinutes: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  hours$ = this.elapsedHours.pipe(map((hour) => +hour));
  minutes$ = this.elapsedMinutes.pipe(map((minute) => +minute));
  isClockedIn = false;
  modalVisible = false;
  timeClock?: TimeClock;
  dayClockIns: (TimeClock & { facilityName?: string })[] = [];
  allHoursVisible = false;
  allHoursTotalTime = {
    hour: 0,
    minute: 0,
  };
  timeClockError: string | null = null;
  clockInDisabled = false;
  errorDate: string = "";
  timezone: string = "";
  hourChanged = false;
  minuteChanged = false;
  ampmChanged = false;
  clickListener = (e: Event) => {
    if (!this.elementRef.nativeElement.contains(e.target)) {
      this.modalVisible = false;
      document
        .querySelector("app-root")
        ?.removeEventListener("click", this.handler, true);
    }
  };
  handler = this.clickListener.bind(this);
  currentUser!: { id: string; fullname: string };
  currentUserFacilities: string[] = [];
  currentUserid: string[] = [];
  integrationsCount: number = 0;
  currentDate: Date = new Date(new Date().setHours(0, 0, 0, 0)); //Will be update with Facility CurrentDate based on its Timezone

  constructor(
    private formBuilder: FormBuilder,
    private timeClockClient: TimeClockClient,
    private clockInService: ClockInService,
    private router: Router,
    private storageService: StorageService,
    private dialog: MatDialog,
    private elementRef: ElementRef,
    private timeDetailClient: TimeDetailClient, 
    private userService: UserClient,
    private facilityRelationClient: FacilityRelationsClient,
  ) {}

  async ngOnInit() {
    this.currentUser = JSON.parse(this.storageService.getUser()!);
    this.currentUserFacilities = (
      await firstValueFrom(
        this.userService.getUserFacilities(this.currentUser.id)
      )
    ).map((item) => item.facilityid);
    this.currentUserid = (
      await firstValueFrom(
        this.userService.getUserFacilities(this.currentUser.id)
      )
    ).map((item) => item.userid);
    await this.getTimeClockData();

    const selectedFacilityId = this.getInitiallySelectedFacilityId();

    if (selectedFacilityId) {
      this.setTimeZone(selectedFacilityId);
    }

    await this.formatAllHours();
    await this.buildForm(selectedFacilityId);
    this.startTimer();
    this.updateDropdowns();
  }

  async checkIntegrationsAsync(selectedFacilityId : string){
    const response = await firstValueFrom(
      this.facilityRelationClient.getIntegrations({ facilityId: selectedFacilityId })
    );
    
    this.integrationsCount = response.data
    .filter(x => {
      return x.interfacetypeid == 1 && x.clockinout == true && (!x.effectivethru || x.effectivethru && new Date(x.effectivethru) >= this.currentDate);
    }).length;
}

  /**
   * Gets the facility id, in priority:
   * 1. facility id for currently running timeclock
   * 2. facility selected in form
   * 3. facility selected in storage
   * @returns the facilityId to use for the initial selection of the facility dropdown
   */
  private getInitiallySelectedFacilityId(): string {
    const facilityIdForCurrentlyRunningClock: string | false | undefined =
      !this.timeClock?.iscomplete && this.timeClock?.facilityid;
    return (
      facilityIdForCurrentlyRunningClock ||
      this.storageService.getSelectedFacilityId() ||
      this.form.get("facilityId")?.value
    );
  }

  async facilitySelected($event: any) {
    this.form.get("facilityId")?.setValue($event.value);
    this.setTimeZone($event.value);
    await this.buildForm($event.value);
  }

  setTimeZone(facilityId: string) {
    this.timezone =
      this.storageService
        .getFacilities()
        .find((facility) => facility.facilityId === facilityId)?.timezone ||
      "US/Eastern";
  }

  async getTimeClockData() {
    this.timeClock = await firstValueFrom(this.timeClockClient.mostRecent());
    this.dayClockIns = (
      await firstValueFrom(this.timeClockClient.getDaysTimeClocks())
    ).data;

    if (this.timeClock) {
      const currentDate = new Date(
        formatInTimeZone(new Date(), this.timezone, "yyyy-MM-dd HH:mm:ss")
      );
      currentDate.setHours(0, 0, 0, 0);
      const clockInDate = new Date(
        formatInTimeZone(
          new Date(this.timeClock.timein + "Z"),
          this.timezone,
          "yyyy-MM-dd HH:mm:ss"
        )
      );

      clockInDate.setHours(0, 0, 0, 0);

      if (!this.timeClock.iscomplete && isBefore(clockInDate, currentDate)) {
        this.isClockedIn = false;
        this.clockInDisabled = true;
        this.errorDate = format(clockInDate, DateFormat);
      } else {
        this.clockInDisabled = false;
      }

      this.isClockedIn =
        this.timeClock.timein !== null && this.timeClock.timeout === null;
    }
  }

  async topTimeClockClick(clockType: string) {
    switch (clockType) {
      case "in":
        if (this.modalVisible && !this.isClockedIn) {
          this.modalVisible = false;
        } else {
          await this.buildForm(this.getInitiallySelectedFacilityId());
          this.modalVisible = true;
        }
        break;
      case "out":
        if (this.modalVisible && this.isClockedIn) {
          this.modalVisible = false;
        } else {
          await this.buildForm(this.getInitiallySelectedFacilityId());
          this.modalVisible = true;
        }
        break;
    }
    if (this.modalVisible) {
      document
        .querySelector("app-root")
        ?.addEventListener("click", this.handler, true);
    }
  }

  async formatAllHours() {
    if (this.dayClockIns.length > 0) {
      let totalMinutes = 0;
      const facilities = await firstValueFrom(this.facilities);

      this.dayClockIns = this.dayClockIns
        .filter((clockIn) => clockIn.timeout !== null)
        .map((clockIn) => {
          if ("facilityName" in clockIn) {
            return clockIn;
          }

          const facility = facilities.find(
            (facility) => facility.id === clockIn.facilityid
          );

          if (clockIn.timein && clockIn.timeout) {
            totalMinutes += differenceInMinutes(
              new Date(clockIn.timeout!),
              new Date(clockIn.timein)
            );
          }

          return {
            ...clockIn,
            facilityName: facility?.name,
          };
        });

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      this.allHoursTotalTime.hour = hours;
      this.allHoursTotalTime.minute = minutes;
    }
  }

  formatDate() {
    const date = new Date(
      formatInTimeZone(new Date(), this.timezone, "yyyy-MM-dd HH:mm:ss.SSS")
    );
    const ampm = date.getHours() > 12 ? "pm" : "am";

    date.setHours(date.getHours() === 0 ? 12 : date.getHours());
    return { date, ampm };
  }

  async buildForm(selectedFacilityId: string) {

    await this.checkIntegrationsAsync(selectedFacilityId);

    const { date, ampm } = this.formatDate();

    this.form = this.formBuilder.group({
      facilityId: [selectedFacilityId],
      hour: [date.getHours()],
      minute: [date.getMinutes()],
      ampm: [ampm],
    });

    this.form
      .get("hour")!
      .setValue(
        date.getHours() < 10
          ? `0${date.getHours()}`
          : date.getHours() > 12
          ? date.getHours() % 12
          : date.getHours()
      );
    this.form
      .get("minute")!
      .setValue(
        date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()
      );

    if (this.isClockedIn) {
      this.form.get("facilityId")!.disable();
    }

    if(!this.integrationsCount && !this.isClockedIn){
      this.clockInDisabled = true;
    }
    else{
      this.clockInDisabled = false;
    }

    if (this.clockInDisabled) {
      this.form.get("hour")!.disable();
      this.form.get("minute")!.disable();
      this.form.get("ampm")!.disable();
      this.timeClockError = this.integrationsCount ? NotClockedOutPreviousDayError : null;
    }

    setTimeout(() => this.reloadAmPm$.next(), 100);
  }

  navigateToErrorTimeCard() {
    this.router.navigate([`/time-card`], {
      queryParams: { date: this.errorDate },
    });
  }

  updateDropdowns() {
    setInterval(() => {
      const { date } = this.formatDate();

      if (!this.hourChanged) {
        this.form
          .get("hour")!
          .setValue(
            date.getHours() < 10
              ? `0${date.getHours()}`
              : date.getHours() > 12
              ? date.getHours() % 12
              : date.getHours()
          );
      }

      if (!this.minuteChanged) {
        this.form
          .get("minute")!
          .setValue(
            date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes()
          );
      }

      if (!this.ampmChanged) {
        this.form.get("ampm")!.setValue(date.getHours() > 12 ? "pm" : "am");
      }
    }, 1000);
  }

  startTimer() {
    setInterval(() => {
      if (this.isClockedIn) {
        let hour = this.form.get("hour")?.value;
        const minute = this.form.get("minute")?.value;
        const ampm = this.form.get("ampm")?.value;

        if (ampm === "am" && hour === 12) {
          hour = 0;
        }

        let actionTimeSet = new Date();
        actionTimeSet.setHours(
          ampm === "pm" && hour !== 12 ? hour + 12 : hour,
          minute
        );

        const currenTime = new Date(
          formatInTimeZone(new Date(), this.timezone, "yyyy-MM-dd HH:mm:ss")
        );

        actionTimeSet.setDate(currenTime.getDate());

        const actionTime = new Date(
          this.getUTCTimeFromTimeZoneDateTime(actionTimeSet)
        ).toISOString();

        const totalMinutes = differenceInMinutes(
          new Date(actionTime.slice(0, -1)),
          new Date(this.timeClock!.timein)
        );

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        this.elapsedHours.next(hours);
        this.elapsedMinutes.next(minutes);
      }
    }, 1000);
  }

  onHourChange($event: any) {
    const value = $event.target.value;

    if (value < this.hourMin) {
      this.form.get("hour")!.setValue(this.hourMin);
    }

    if (value > this.hourMax) {
      this.form.get("hour")!.setValue(this.hourMax);
    }

    this.hourChanged = true;
  }

  onMinuteChange($event: any) {
    const value = $event.target.value;

    if (value < this.minuteMin) {
      this.form.get("minute")!.setValue(this.minuteMin);
    }

    if (value > this.minuteMax) {
      this.form.get("minute")!.setValue(this.minuteMax);
    }

    this.minuteChanged = true;
  }

  ampmChangedEvent($event: any) {
    this.ampmChanged = true;
  }

  async toggleTimeClock() {
    let hour = this.form.get("hour")?.value;
    const minute = this.form.get("minute")?.value;
    const ampm = this.form.get("ampm")?.value;

    if (ampm === "am" && hour === 12) {
      hour = 0;
    }

    const currenTime = new Date(
      formatInTimeZone(new Date(), this.timezone, "yyyy-MM-dd HH:mm:ss")
    );

    let actionTimeSet = new Date();
    actionTimeSet.setHours(
      ampm === "pm" && hour !== 12 ? hour + 12 : hour,
      minute
    );

    actionTimeSet.setDate(currenTime.getDate());

    const actionTime = new Date(
      this.getUTCTimeFromTimeZoneDateTime(actionTimeSet)
    ).toISOString();

    const actionTimeDate = new Date(
      formatInTimeZone(new Date(actionTime), this.timezone, "yyyy-MM-dd HH:mm:ss")
    );

    let initialActionDate = null;

    if (this.isClockedIn) {
      initialActionDate = new Date(
        formatInTimeZone(
          new Date(this.timeClock!.timein + "Z"),
          this.timezone,
          "yyyy-MM-dd HH:mm:ss"
        )
      );
    }

    if (initialActionDate && isAfter(initialActionDate, actionTimeDate)) {
      this.timeClockError = ClockOutBeforeClockInError;
      return;
    } else if (isAfter(actionTimeDate, currenTime)) {
      this.timeClockError = ClockInOutAfterCurrentTimeError;
      return;
    } else if (!this.isClockedIn) {
      try {
        initialActionDate = new Date(
          formatInTimeZone(
            new Date(),
            this.timezone,
            "yyyy-MM-dd HH:mm:ss"
          )
        );
        const timeinDate=format(new Date( initialActionDate),DateFormat)
        const filters = { userId: this.currentUserid[0], facilityId: this.form.get("facilityId")?.value, date: format(new Date(timeinDate),DateFormat) };
        const timeDetails = await firstValueFrom(this.timeDetailClient.get(filters));
        const timeClock: TimeClock = {
          facilityid: this.form.get("facilityId")!.value,
          timein: actionTime,
          timecarddate:timeinDate,
          usertimecardid: timeDetails?.id,
        };
        await firstValueFrom(this.timeClockClient.add(timeClock));
        this.clockInService.updateTimeClock(timeClock);
        this.timeClockError = null;
        this.ngOnInit();
      } catch (err: any) {
        this.dialog.open(ConfirmationModalComponent, {
          data: {
            title: "Error",
            message: err?.error,
            buttonText: "OK",
            hideCancelButton: true,
            isError: true,
          },
        });
      }
    } else {
      this.timeClock!.timeout = actionTime;
      try {
        const timeClock: TimeClock = { ...this.timeClock!, iscomplete: true };
        await firstValueFrom(this.timeClockClient.edit(timeClock));
        this.clockInService.updateTimeClock(timeClock);
        this.timeClockError = null;
        this.ngOnInit();
      } catch (err: any) {
        this.dialog.open(ConfirmationModalComponent, {
          data: {
            title: "Error",
            message: err?.error,
            buttonText: "OK",
            hideCancelButton: true,
            isError: true,
          },
        });
      }
    }
  }

  getUTCTimeFromTimeZoneDateTime(date: Date) {
    const actionTimeSetDate = format(date, "yyyy-MM-dd");
    const actionTimeSetTime = format(date, "HH:mm");
    const timezoneOffset = getTimezoneOffset(this.timezone) / 60 / 1000;
    const hoursOffset = Math.floor(Math.abs(timezoneOffset) / 60)
      .toString()
      .padStart(2, '0');
    const minutesOffset = Math.abs(timezoneOffset % 60)
      .toString()
      .padStart(2, '0');

    const offsetSign = timezoneOffset >= 0 ? "+" : "-";
    const formattedOffset = `${offsetSign}${hoursOffset}:${minutesOffset}`;

    return `${actionTimeSetDate}T${actionTimeSetTime}${formattedOffset}`;
  }
}
